import { Player, PlayerAPI, PlayerConfig } from 'bitmovin-player';
import { UIFactory, UIManager } from 'bitmovin-player-ui';
import { MutableRefObject, RefCallback } from 'react';
import { BitmovinPlayerProps, IBitmovinPlayerWrapper } from '../types/iBasePlayer';
import AdvertisingBitmovinModule from 'bitmovin-player/modules/bitmovinplayer-advertising-bitmovin';
import { playerSSAIManager } from '../../../utils/SSAI';
import cdnHandler from '../../../helpers/CDNHandler';
import { guardPlayerApi, isPlayerAlive, markPlayerDestroyed } from '../../../helpers/playerGuards';
import { log } from '@intigral/utils';

/** Upper bound for waiting on Bitmovin's async `destroy()` before we clean up the DOM/UI ourselves. */
const DESTROY_SAFETY_TIMEOUT_MS = 3000;

Player.addModule(AdvertisingBitmovinModule);

export const setRef = <T>(ref: RefCallback<T> | MutableRefObject<T>, value: T) => {
    if (typeof ref === 'function') {
        ref(value);
    } else {
        ref.current = value;
    }
};

export const initializePlayerUi = (
    player: PlayerAPI,
    playerConfig: PlayerConfig,
    customUi?: BitmovinPlayerProps['customUi']
) => {
    if (playerConfig.ui === false) {
        return;
    }

    // If a custom UI container is provided, use it instead of the default UI.
    if (customUi && 'containerFactory' in customUi) {
        return new UIManager(player, customUi.containerFactory(), playerConfig.ui);
    }
    // If custom UI variants are provided, use them instead of the default UI.
    else if (customUi && 'variantsFactory' in customUi) {
        return new UIManager(player, customUi.variantsFactory(), playerConfig.ui);
    } else {
        return UIFactory.buildDefaultUI(player, playerConfig.ui);
    }
};

export const convertConfig = (originalConfig: PlayerConfig) => {
    const convertedConfig: PlayerConfig = {
        ...originalConfig,
    };

    // By default Bitmovin player assumes there is `bitmovinplayer-ui.css` and `bitmovinplayer-ui.js` files hosted on the same domain
    // and tries to use them. Since this is a React wrapper it should work standalone.
    // Disable loading the default `bitmovinplayer-ui.css` and `bitmovinplayer-ui.js` from the same domain by the player.
    convertedConfig.ui = false;

    return convertedConfig;
};

export const initializePlayer = (
    containerElement: HTMLDivElement,
    videoElement: HTMLVideoElement,
    convertedConfig: PlayerConfig
) => {
    // Guard the public API *before* anything (UIManager, SmartLib, NPAW, hooks) can capture method
    // references or attach listeners, so late calls after destroy() never throw API_NOT_AVAILABLE.
    const player = guardPlayerApi(new Player(containerElement, convertedConfig));
    player.setVideoElement(videoElement);

    return player;
};

export const preparePlayerElements = (rootContainerElement?: HTMLDivElement) => {
    const createdPlayerContainerElement = document.createElement('div');
    const createdVideoElement = document.createElement('video');
    if (rootContainerElement) rootContainerElement.appendChild(createdPlayerContainerElement);
    createdPlayerContainerElement.appendChild(createdVideoElement);

    if (playerSSAIManager.canRunSSAI) playerSSAIManager.setVideoElement(createdVideoElement);

    return {
        createdPlayerContainerElement,
        createdVideoElement,
    };
};

/**
 * Tears down a player instance created by `createBitmovinPlayer`.
 *
 * Bitmovin's `destroy()` invalidates the public API synchronously and fires `PlayerEvent.Destroy`
 * asynchronously, so anything that still holds the instance (bitmovin-player-ui timers/handlers,
 * SmartLib, NPAW, our own Destroy handlers) would hit a dead API. The instance is API-guarded
 * from creation (see `guardPlayerApi`), so those late calls degrade to no-ops; here we also detach
 * the integrations that we own *while the player is still alive*.
 */
export const destroyPlayer = (
    player: PlayerAPI,
    rootContainerElement: HTMLDivElement,
    playerContainerElement: HTMLDivElement,
    uiManager: UIManager | undefined
) => {
    playerContainerElement.style.display = 'none';
    const removePlayerContainerElement = () => {
        try {
            if (rootContainerElement.contains(playerContainerElement)) {
                rootContainerElement.removeChild(playerContainerElement);
            }
        } catch (e) {
            log.debug('[destroyPlayer] container already removed:', e);
        }
    };

    // Detach CDN-bound player listeners and stop the SmartLib session bound to *this* player while
    // its API is still usable. Doing it from the Destroy event is too late: SmartLib calls
    // `player.off(...)` there and the instance is already gone.
    cdnHandler.destroyBoundEvents();
    cdnHandler.stopCDNSessionFor(player);

    // Make sure guards are in place even for instances that were not created through
    // `initializePlayer` (idempotent for the ones that were).
    guardPlayerApi(player);

    let finished = false;
    const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(safetyTimer);

        markPlayerDestroyed(player);
        try {
            // Keep UI listeners attached until destroy() has fired Destroy so components such as
            // TimelineMarkersHandler / SeekBar get the chance to clear their 1s timers.
            uiManager?.release();
        } catch (e) {
            log.debug('[destroyPlayer] ignored error during UI release:', e);
        }
        removePlayerContainerElement();
    };
    // `finish` is only ever invoked after this assignment (destroy() settles asynchronously).
    const safetyTimer = setTimeout(() => {
        log.debug('[destroyPlayer] destroy() did not settle in time; finishing teardown anyway');
        finish();
    }, DESTROY_SAFETY_TIMEOUT_MS);

    if (!isPlayerAlive(player)) {
        log.debug('[destroyPlayer] player already destroyed; releasing UI/container only');
        finish();
        return;
    }

    try {
        player.destroy().then(finish, finish);
    } catch (e) {
        log.debug('[destroyPlayer] destroy failed:', e);
        finish();
    }
};

export function createBitmovinPlayer(config: PlayerConfig): IBitmovinPlayerWrapper {
    const { createdPlayerContainerElement, createdVideoElement } = preparePlayerElements();
    const convertedConfig = convertConfig(config);
    const initializedPlayer = initializePlayer(createdPlayerContainerElement, createdVideoElement, convertedConfig);

    return {
        createdPlayerContainerElement,
        createdVideoElement,
        convertedConfig,
        initializedPlayer,
    };
}
